import React from 'react';
import ReactDOM from 'react-dom/client';
// FIRST, before any screen: tells the shared clinical module which BFF and which
// sign-in it serves (docs/CLINICAL_KIT_DESIGN.md). Import order is evaluation order.
import './src/lib/clinicalHost.js';
import { ClinicalHostProvider } from '@parsina/framework/clinical';
import { useAppNavigate } from './src/hooks/useAppNavigate';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { IOSDevice } from './ios-frame';
import { ToastProvider, ThemeProvider, useTheme, useToast, SessionCheck, SessionError } from '@parsina/framework';
import { I18nProvider, useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import KitStrings from './src/lib/KitStrings';
import { adoptFontFamily } from '@parsina/framework/clinical/src/constants/fonts';
import { brand, isIranMarket } from '@parsina/framework/ui/lib/brand';
import { brandAsset } from './src/lib/brandAssets';
import useResponsive from '@parsina/framework/clinical/src/hooks/useResponsive';
import WebLayout from './src/components/common/WebLayout';
import MobileBottomNav from './src/components/mobile/MobileBottomNav';
import { getToken, getRefresh, isTokenExpired, handleCallback, redirectToLogin, refreshAccessToken, clearRedirectGuard, parseJwt } from './src/lib/auth.js';
import { bffQuery } from '@parsina/framework/clinical/src/lib/bffClient';
import { KC_BASE_URL, kcBffClientId } from './src/lib/runtimeEnv.js';
import { ensureChatStarted } from './src/lib/chatRuntime.js';
import { loadClinicZones } from './src/lib/clinicZone.js';
import { initPwa } from './src/lib/pwa.js';
import { registerServiceWorker } from './src/lib/registerSw.js';
import InstallPrompt from './src/components/common/InstallPrompt';
import { RootErrorBoundary, AppErrorBoundary, ScreenErrorBoundary } from './src/components/system/ErrorBoundary';
import ConnectivityBanner from './src/components/system/ConnectivityBanner';
import useOnlineStatus from './src/hooks/useOnlineStatus';
import { isOffline } from '@parsina/framework/clinical/src/lib/appError';
import { SessionExpiringDialog, SessionExpiredDialog } from './src/components/system/SessionDialog';
import useSessionLifecycle from './src/hooks/useSessionLifecycle';
import { takeReturnPath, rememberCurrentPathForLogin } from './src/lib/session.js';
import { readPaymentOutcome, stripPaymentParam } from './src/lib/paymentReturn.js';
import { notifyWalletChanged } from '@parsina/framework/clinical/src/hooks/useWalletBalance';

// Screen Imports
import ReceptionScreen from './src/screens/Reception';
import EncountersScreen from './src/screens/Encounters';
import MessagesScreen from './src/screens/Messages';
import PatientDetailScreen from './src/screens/PatientDetail';
import VisitScreen from './src/screens/Visit';
import VoiceRecordScreen from './src/screens/VoiceRecord';
import PrescriptionScreen from './src/screens/Prescription';
import CreditsScreen from './src/screens/Credits';
import AccountingScreen from './src/screens/Accounting';
import MyPageScreen from './src/screens/MyPage';
import { localMyPageString, hasLocalMyPageString } from './src/screens/MyPage/myPageStrings';
import SettingsScreen from './src/screens/Settings';
import PatientLookupScreen from './src/screens/PatientLookup';
import OnboardingScreen from './src/screens/Onboarding';
import PrizeWheelScreen from './src/screens/PrizeWheel';
import { NotFound, Offline as OfflinePage } from './src/screens/System';

import './styles.css';

// ── Paths that use the sidebar web layout ─────────────────────────────────────
// '/patients' stays even though the patient LIST page is gone: this is a startsWith
// match and the patient profile at /patients/:id still needs the sidebar.
// This was an allow-list of the paths that GET the sidebar. It is now the
// complement — the paths that do NOT — and the change is what lets a 404 wear
// the chrome.
//
// The distinction could not arise before: '*' redirected, so an address
// matching nothing never rendered, so nobody had to decide what it looked like.
// Now that it renders a real 404, an allow-list would have given it no
// navigation at all — stranding the doctor on a dead end whose only exits are
// the page's own two buttons. Asking the question the other way round means
// anything unrecognised still arrives inside the app, with the sidebar to leave
// by, which is what makes a wrong turn a wrong turn rather than a trap.
//
// /login is the one page that genuinely owns the viewport: there is no
// authenticated doctor yet to put in a sidebar.
// The list is EMPTY, and that is the honest state: every route this app serves
// belongs to an authenticated doctor. `/login` used to be the exception, and it
// was a fiction — sign-in is Keycloak-hosted (auth.js `redirectToLogin`), the
// two in-app login screens predated the PKCE migration and nothing had routed
// to them since, so they were deleted. If an in-app web login is ever wanted
// again it is built on `redirectToLogin()` / `handleCallback()`, never on a BFF
// OTP pair the web BFF no longer defines.
const BARE_LAYOUT_PREFIXES = [];

// ── Route semantics: every top-level page owns a document title ───────────────
// Longest-prefix match over the pathname; the key mirrors the sidebar's so the
// browser tab, history entries, and the in-app nav all name a page the same way
// — in whatever language is selected.
const PAGE_TITLES = [
  ['/reception',    'nav.items.reception'],
  ['/patients',     'nav.items.patients'],
  ['/lookup',       'patient.lookup.title'],
  ['/encounters',   'nav.items.encounters'],
  ['/messages',     'nav.items.messages'],
  ['/visit',        'nav.items.visit'],
  ['/voice',        'nav.items.voice'],
  ['/prescription', 'nav.items.prescription'],
  ['/credits',      'nav.items.credits'],
  ['/accounting',   'nav.items.accounting'],
  ['/my-page',      'nav.items.myPage'],
  ['/profile',      'nav.items.profile'],
];

// `locale` is in the dependency list so switching language retitles the tab
// without a navigation — the title is UI text like any other.
const usePageTitle = (pathname) => {
  const { t, has, locale } = useTranslation();
  React.useEffect(() => {
    const entry = PAGE_TITLES.find(([prefix]) => pathname.startsWith(prefix));
    const brand = t('nav.brand.name');
    // «صفحهٔ من»'s title is app-local until the kit catalogue carries it.
    const label = (key) => (!has(key) && hasLocalMyPageString(key) ? localMyPageString(locale, key) : t(key));
    document.title = entry
      ? `${label(entry[1])} | ${brand}`
      : `${brand} — ${t('nav.brand.tagline')}`;
  }, [pathname, t, has, locale]);
};

// ── Mobile shell: bottom navigation on the mobile-designed destinations ───────
// DESIGN-CONCEPT-MOBILE v2 §3.1: the bottom nav is the app's one persistent
// chrome, and it lists only screens that have a mobile design. Records and
// flows (visit, voice, prescription) render full-screen without it.
// حسابداری is a tab (it holds the day's income); پیام‌ها and پروفایل sit behind
// «بیشتر» but are still bottom-nav destinations — the bar stays, «بیشتر» lit.
const BOTTOM_NAV_PATHS = ['/reception', '/encounters', '/accounting', '/messages', '/profile', '/my-page'];

// …with one exception. /messages is a top-level destination while it shows the
// conversation LIST, but `?c=` turns it into a §4B content screen — a
// full-screen thread with its own back affordance and a docked composer, which
// the persistent chrome must not sit under. The bottom nav is therefore hidden
// exactly when a conversation is open.
// «صفحهٔ من» likewise: `?section=` is one section's full-screen editor.
const hidesBottomNav = ({ pathname, search }) =>
  (pathname === '/messages' && new URLSearchParams(search).has('c'))
  || (pathname === '/my-page' && new URLSearchParams(search).has('section'));

// Every path the router KNOWS. Anything else is a 404 — and a 404 keeps the
// bottom nav (§B.5, F-X1).
//
// A wrong turn is a wrong turn, not a trap. Without the nav the doctor's only
// exits from a mistyped address are the page's own two buttons, which is a dead
// end wearing a friendly face; the desktop surface has given 404 the sidebar
// for exactly this reason since the catch-all stopped redirecting.
const KNOWN_PREFIXES = [
  '/reception', '/patients', '/lookup', '/encounters', '/messages',
  '/visit', '/voice', '/prescription', '/credits', '/accounting', '/profile', '/my-page',
  '/dashboard',
];
const isUnknownPath = ({ pathname }) =>
  pathname !== '/' && !KNOWN_PREFIXES.some(p => pathname === p || pathname.startsWith(`${p}/`));

// ── The gateway return leg, wherever it lands ─────────────────────────────────
// `Credits` reports `?payment=` on its own screen, because it also has a
// transaction list to refetch. This is the same report for every OTHER route,
// and it exists because the return is not guaranteed to arrive on /credits: a
// top-up begun mid-visit sends the doctor back to the visit, and a return that
// lands in a browsing context without a token goes through Keycloak first and
// comes back to whatever path was remembered at boot.
//
// The doctor paid; being told nothing is the failure this fixes. The parameter
// decides the SENTENCE only — the balance is refreshed from the wallet, which
// is the sole authority on whether the money arrived.
const usePaymentOutcomeToast = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const location = useLocation();

  React.useEffect(() => {
    // /credits owns its own copy of this — two toasts for one payment is worse
    // than none.
    if (location.pathname === '/credits') return;
    const settled = readPaymentOutcome(location.search);
    if (!settled) return;

    stripPaymentParam();
    if (settled === 'success') toast.success(t('credits.paymentDone'));
    else toast.error(t('errors.payment.failedNoCharge'));
    // Every mounted wallet reads the truth from the server.
    notifyWalletChanged();
  }, [location.pathname, location.search, toast, t]);
};

// ── Inner app — rendered only after auth is ready ─────────────────────────────
//
// The doctor panel as a HOST of the clinical module: every clinical component —
// the visit room, the patient record, and the kit pieces پذیرش and the other
// screens share with them — raises its navigation on this provider. Intents
// resolve through the panel's own ROUTES (useAppNavigate); «back» is one history
// step. Outside AppShellBody because that body returns from two places.
const AppShell = () => {
  const location = useLocation();
  const rrNavigate = useNavigate();
  const appNavigate = useAppNavigate();
  // useAppNavigate returns a fresh closure per render; the provider hands out a
  // stable one so consumers do not re-render on every shell render.
  const navRef = React.useRef(appNavigate);
  navRef.current = appNavigate;
  const navigate = React.useCallback((to, data) => navRef.current(to, data), []);
  const back = React.useCallback(() => rrNavigate(-1), [rrNavigate]);
  return (
    <ClinicalHostProvider navigate={navigate} back={back} pathname={location.pathname}>
      <AppShellBody />
    </ClinicalHostProvider>
  );
};

const AppShellBody = () => {
  const { isMobile } = useResponsive();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const connection = useOnlineStatus();
  const session = useSessionLifecycle();

  usePageTitle(location.pathname);
  usePaymentOutcomeToast();

  const showNav = isMobile
    && (BOTTOM_NAV_PATHS.includes(location.pathname) || isUnknownPath(location))
    && !hidesBottomNav(location);
  const inWebLayout = !BARE_LAYOUT_PREFIXES.some(p => location.pathname.startsWith(p));

  // The chat engine is session-scoped chrome, not a screen's concern: started
  // here, once, after auth is ready — the same place and for the same reason
  // the realtime event stream is — so the sidebar and bottom-nav unread badges
  // are live everywhere, not only while /messages happens to be open.
  React.useEffect(() => { ensureChatStarted().catch(() => { /* the screen surfaces it */ }); }, []);

  // The clinic's zone for the visit room's «امروز» (src/lib/clinicZone.js): asked
  // once, after auth is ready, so it is known before a visit opens. It never
  // rejects — unanswered, the kit stays on the locale's zone, as before.
  React.useEffect(() => { loadClinicZones(); }, []);

  // One boundary around the routed screen, INSIDE the chrome (both the mobile
  // frame and the desktop sidebar wrap it below). A crash in one screen
  // therefore costs that screen and nothing else: the nav, the doctor's balance
  // and the unread badges all keep working, and walking to another page is the
  // recovery. `resetKey` is the pathname, so simply navigating away clears the
  // latched error — without it React keeps a boundary in its failed state for
  // the lifetime of the app, and the doctor would carry the broken page around
  // with them.
  const routes = (
    <ScreenErrorBoundary resetKey={location.pathname} onGoHome={() => navigate('/reception')}>
    <Routes>
      <Route path="/"                        element={<Navigate to="/reception" replace />} />
      {/* Old «داشبورد» bookmarks. This used to be served by the '*' catch-all,
          which redirected everything to پذیرش; now that '*' is an honest 404,
          the one legacy path we actually want to forward needs saying out loud.
          A redirect is right HERE and wrong for '*': this address did mean the
          reception desk once, so sending it there keeps a promise. An address
          that never existed has no promise to keep. */}
      <Route path="/dashboard"               element={<Navigate to="/reception" replace />} />
      <Route path="/reception"               element={<ReceptionScreen />} />
      {/* Optional :tab? — the record's section is part of the URL, so refresh /
          back / a shared link lands on the same section (overview when absent). */}
      <Route path="/patients/:id/:tab?"      element={<PatientDetailScreen />} />
      {/* «جست‌وجوی بیمار» — a mobile content screen (S12). It renders at any
          width rather than forking: there is nothing about one field and a
          result list that needs a second design. */}
      <Route path="/lookup"                  element={<PatientLookupScreen />} />
      <Route path="/encounters"              element={<EncountersScreen />} />
      <Route path="/messages"                element={<MessagesScreen />} />
      <Route path="/visit/:patientId"        element={<VisitScreen />} />
      <Route path="/voice/:patientId"        element={<VoiceRecordScreen />} />
      <Route path="/prescription/:patientId" element={<PrescriptionScreen />} />
      <Route path="/credits"                 element={<CreditsScreen />} />
      <Route path="/accounting"              element={<AccountingScreen />} />
      <Route path="/my-page"                 element={<MyPageScreen />} />
      <Route path="/profile"                 element={<SettingsScreen />} />
      {/* An unresolvable address says so. Redirecting it to پذیرش made a stale
          bookmark look like it still worked, hid every route removed in a
          refactor, and meant a bad link was never once reported — because
          nobody could tell it was bad. */}
      <Route path="*"                        element={<NotFound />} />
    </Routes>
    </ScreenErrorBoundary>
  );

  // The connection notice is app chrome, not a screen's concern: one bar, at
  // the top, on both surfaces. Rendered here for the same reason the chat
  // runtime is started here — a dropped connection is a fact about the session,
  // and every screen failing to explain it independently is what this replaces.
  const connectivityBar = (
    <ConnectivityBanner
      state={connection.state}
      recovered={connection.recovered}
      onAcknowledgeRecovery={connection.acknowledgeRecovery}
      onRetry={connection.check}
    />
  );

  // Session dialogs are fixed-position overlays, so they render as siblings of
  // the shell on both surfaces rather than being threaded into either layout.
  // They deliberately sit OVER the app instead of replacing it — the screen
  // underneath is the doctor's unsaved work, and it stays visible.
  const sessionOverlay = (
    <>
      {session.isExpiring && (
        <SessionExpiringDialog
          secondsLeft={session.secondsLeft}
          onExtend={session.extend}
          onDismiss={session.dismissWarning}
          busy={session.busy}
        />
      )}
      {session.isExpired && (
        <SessionExpiredDialog onSignIn={session.signInAgain} busy={session.busy} />
      )}
    </>
  );

  // ── Mobile: wrap in iOS frame ─────────────────────────────────────────────
  if (isMobile) {
    const content = (
      <div className="screen-area">
        {connectivityBar}
        {/* Route wrapper — flex column so child screens with flex:1 fill the space */}
        <div style={{ flex: 1, minHeight: 0, position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column', background: '#f5f7fa' }}>
          {routes}
        </div>
        {showNav && <MobileBottomNav />}
        <InstallPrompt />
      </div>
    );
    return (
      <>
        <IOSDevice dark>{content}</IOSDevice>
        {sessionOverlay}
      </>
    );
  }

  // ── Desktop: sidebar layout for main screens, bare for others ────────────
  // The shell owns the viewport (WebLayout is now height:100%), so the bar can
  // sit above the sidebar and push it down instead of floating over the page
  // header.
  return (
    <>
      <div style={{ width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {connectivityBar}
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {inWebLayout ? <WebLayout>{routes}</WebLayout> : routes}
        </div>
      </div>
      {sessionOverlay}
    </>
  );
};

// ── Root: handles auth, then mounts the router ────────────────────────────────
const App = () => {
  const [authReady, setAuthReady] = React.useState(false);
  const [authError, setAuthError] = React.useState(null);
  const [needsOnboarding, setNeedsOnboarding] = React.useState(false);
  // A wheel this doctor was offered and has not spun. It rides in on the boot
  // query (docs/ONBOARDING_PRIZE_WHEEL_PLAN.md §5.3) because the two doctors
  // who need it — one who closed the app mid-onboarding, one who was already
  // on the platform when the campaign started — are otherwise indistinguishable
  // from everyone else by the time the router mounts.
  const [pendingDraw, setPendingDraw] = React.useState(null);
  const [bootOffline, setBootOffline] = React.useState(false);
  const { setAppearance } = useTheme();
  const { t, setLocale } = useTranslation();

  React.useEffect(() => {
    (async () => {
      const urlParams = new URLSearchParams(window.location.search);

      if (urlParams.get('auth_error')) {
        window.history.replaceState({}, '', '/');
        setAuthError(t('errors.auth.connect'));
        return;
      }

      // Booting with no network. This is a real path now that the app is a PWA:
      // the service worker hands back the cached shell, React mounts, and then
      // every branch below ends in `redirectToLogin()` — a full navigation to
      // sso.itechtrade.ir, which cannot resolve. The doctor watched the app
      // they had just opened be replaced by the browser's own connection-error
      // page, with no way back to it. Say what happened instead, and offer the
      // one action that helps.
      if (isOffline()) { setBootOffline(true); return; }

      // ── Remember WHERE before handing the tab to Keycloak ────────────────
      //
      // Signing in is a full document navigation, so every boot branch below
      // that ends in `redirectToLogin()` destroys the address it was called
      // from. `rememberReturnPath` existed but only `signInAgain` (session
      // EXPIRY) ever called it — so an expiring session came back to the right
      // screen and a COLD START never did. Every PWA shortcut, every shared
      // /patients/:id, every /visit/:id opened from a message, and every return
      // leg of a top-up landed on پذیرش instead.
      //
      // One line, called before each redirect, is the whole fix. `/` is skipped
      // because that is where a sign-in lands anyway.
      const signIn = async () => {
        rememberCurrentPathForLogin();
        await redirectToLogin();
      };

      if (urlParams.has('code')) {
        const ok = await handleCallback();
        // A failed exchange re-enters the flow. The remembered path is still
        // in localStorage and untouched by `handleCallback`, so the retry keeps
        // the destination rather than resetting it to this callback URL — which
        // is why this branch calls `redirectToLogin` and not `signIn`.
        if (!ok) { await redirectToLogin(); return; }

        // Put the doctor back where they were interrupted. Keycloak's
        // redirect_uri is the origin, so every re-login used to land on پذیرش
        // — a shrug on a list screen and a real cost on /visit/:patientId,
        // where the doctor has to find the patient and re-open the encounter
        // having just been interrupted mid-note.
        //
        // `replaceState`, not a navigation: the router has not mounted yet, so
        // rewriting the address here is what the FIRST render reads. Doing it
        // afterwards would paint پذیرش and then jump, and leave the OIDC
        // callback in the history stack for the back button to return to.
        const returnTo = takeReturnPath();
        if (returnTo && returnTo !== '/') window.history.replaceState({}, '', returnTo);
      }

      let token = getToken();
      // A new tab or a reopened browser has no access token but may hold the
      // 30-day refresh token: sign in silently from it first (PM-0158).
      if (!token && getRefresh() && await refreshAccessToken()) token = getToken();
      if (!token) { await signIn(); return; }

      if (isTokenExpired(token)) {
        const refreshed = getRefresh() ? await refreshAccessToken() : false;
        if (!refreshed) { await signIn(); return; }
        token = getToken();
      }

      parseJwt(token);
      clearRedirectGuard();

      // The appearance rides along on the onboarding check rather than in its
      // own request: it costs nothing here, and it is what makes the accent AND
      // the UI language follow the doctor to a device whose localStorage has
      // never seen them.
      let isOnboarded = false;
      try {
        const data = await bffQuery(`query {
          checkOnboardingStatus {
            isOnboarded
            pendingPrizeDraw {
              id wheelCode title subtitle status
              segments { index label kind amount }
              segmentIndex kind amount smartVisits
              whatsappMessages baleMessages smsMessages retry
              balance { balance currency }
              endsAtMs
            }
          }
          me { settings { appearance { theme fontSize language fontFamily } } }
        }`);
        isOnboarded = data?.checkOnboardingStatus?.isOnboarded ?? false;
        // Only for a doctor who is PAST onboarding: the wizard asks
        // `myPrizeDraws` for itself and shows the wheel as its own stage, so
        // honouring this here too would race it into a second screen. And only
        // in the Iranian market: the wheel pays in the Iranian campaign's
        // credit, and a deployment outside it (`market: 'intl'`) runs no
        // campaign — a draw the BFF still answers with is never shown.
        const draw = data?.checkOnboardingStatus?.pendingPrizeDraw;
        if (isIranMarket() && isOnboarded && draw?.id && (draw.segments?.length ?? 0) >= 2) setPendingDraw(draw);
        const appearance = data?.me?.settings?.appearance;
        if (appearance) {
          setAppearance(appearance);
          // The server copy is the record; the pre-paint script only had the
          // local cache to go on. Applying it here is what makes the choice
          // follow the doctor across devices. `setLocale` ignores an empty or
          // unsupported tag, so a service that has never stored one is a no-op.
          if (appearance.language) setLocale(appearance.language);
          // The typeface is the app's own (not the framework's) — see
          // src/constants/fonts.js. Same rule: server copy wins once known.
          if (appearance.fontFamily) adoptFontFamily(appearance.fontFamily);
        }
      } catch (err) {
        // An auth failure is NOT "this doctor has not onboarded". Lumping the two
        // together is how a stale or wrong-issuer token put the onboarding wizard
        // in front of doctors with years of data — the honest answer to a rejected
        // token is to log in again.
        const code = err?.extensions?.code;
        if (code === 'UNAUTHENTICATED' || err?.status === 401) {
          await signIn();
          return;
        }
        // Any other failure: boot must not depend on this read, so leave the cached
        // appearance and language (or the defaults) in place. Assuming
        // not-onboarded stays wrong for a transient outage, but the onboarding
        // screen is recoverable and a blocked boot is not.
        isOnboarded = false;
      }
      setNeedsOnboarding(!isOnboarded);
      setAuthReady(true);
    })();
  }, []);

  const handleOnboardingComplete = async () => {
    await refreshAccessToken().catch(() => {});
    const token = getToken();
    const roles = parseJwt(token)?.resource_access?.[kcBffClientId()]?.roles ?? [];
    console.log('[onboardingComplete] refreshed token roles:', roles);
    if (!roles.includes('doctor')) {
      // KC role propagation may lag slightly; retry once after a short pause
      await new Promise(r => setTimeout(r, 1500));
      await refreshAccessToken().catch(() => {});
    }
    setNeedsOnboarding(false);
  };

  // Retrying is a reload on purpose: the boot sequence has already run and
  // bailed, and re-entering it from a React state change would mean unpicking
  // which of its steps had partially completed. A reload is the only reset that
  // is genuinely a reset — and by the time the doctor presses it, the shell is
  // in the service-worker cache, so it costs nothing.
  if (bootOffline) {
    return <OfflinePage fullscreen onRetry={() => window.location.reload()} />;
  }

  // The session screens are the kit's (packages/DESIGN.md §"Session check") —
  // one card, one motion, in every Parsina panel. Only the words are ours.
  if (authError) {
    return (
      <SessionError
        logo={brandAsset('logo')}
        brandName={t('nav.brand.name')}
        title={t('errors.auth.title')}
        body={authError}
        actionLabel={t('common.actions.retry')}
        onAction={() => { window.location.href = '/'; }}
        // Derived, not hardcoded: this label sat on the retired SSO host for
        // weeks after the cutover and told users to trust the wrong domain.
        host={KC_BASE_URL.replace(/^https?:\/\//, '')}
      />
    );
  }

  if (!authReady) {
    return (
      <SessionCheck
        logo={brandAsset('logoWhite')}
        brandName={t('nav.brand.name')}
        panelName={t('nav.brand.system')}
        title={t('auth.redirect.title')}
        subtitle={t('auth.redirect.subtitle')}
        stuckLabel={t('auth.redirect.notRedirected')}
        manualLabel={t('auth.redirect.clickHere')}
        onManualSignIn={() => { rememberCurrentPathForLogin(); redirectToLogin(); }}
      />
    );
  }

  if (needsOnboarding) {
    return <OnboardingScreen onComplete={handleOnboardingComplete} />;
  }

  // Same place, same reason: there is no shell yet, and the wheel is not a
  // route. «ادامه» clears it and the doctor carries on into /reception with
  // nothing else changed — the wheel never alters what comes after it.
  if (pendingDraw) {
    return <PrizeWheelScreen draw={pendingDraw} onDone={() => setPendingDraw(null)} />;
  }

  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
};

// ThemeProvider is outermost so the accent preset is applied from the cache
// before the first paint — including on the auth and onboarding screens, which
// render long before any settings query resolves.
//
// I18nProvider sits directly inside it, ABOVE ToastProvider and App, for the
// same reason and one more: every screen below it, including the auth error and
// the onboarding flow, reads its copy through `t`. It resolves the language
// from the same cache the pre-paint script in index.html used, so `dir` never
// changes between the first frame and the first render.
// Before the first render: capture `beforeinstallprompt` (it can fire while
// React is still mounting) and register the service worker (production only).
initPwa();
registerServiceWorker();

// ── /docs — the design system platform ────────────────────────────────────────
// Mounted instead of, not inside, the product tree: the catalog renders only the
// design system, touches no BFF and shows no patient data, so sending a designer
// through Keycloak to reach it would buy nothing and cost them the visit. It is
// lazily imported so the docs never enter the product's first bundle.
//
// The branch is on the path at mount time, which is exactly right for a page
// that owns its own router: once inside /docs, the docs router handles every
// subsequent navigation, and leaving it is a full document load back into the app.
const DocsApp = React.lazy(() => import('./src/docs'));

// ── /erx/tamin-return — back from تأمین's own login page ──────────────────────
// Also mounted instead of the product tree, for the same reason as /docs and one
// more: a Keycloak round trip here would drop the `?session=ok` the page exists
// to read. It only tells the tab holding the e-prescription card to carry on.
const TaminReturnPage = React.lazy(() => import('./src/screens/ErxReturn/TaminReturnPage'));

// Neither is mounted for every brand (docs/rebranding/CONTRACT.md). The design
// catalogue is written in Persian and about Parsina, so a brand that does not
// offer Persian does not serve it; the تأمین return is an Iranian rail, so it
// exists only in the Iranian market. Either path then falls through to the
// product, which answers it as the 404 any unknown address gets.
const offersPersian = () => {
  const offered = brand().locales;
  return !offered || offered.includes('fa');
};

// Two boundaries, at two altitudes (see components/system/ErrorBoundary):
// `RootErrorBoundary` wraps the providers themselves and is the only thing that
// can catch one of them throwing — so its copy is static, because `t` is exactly
// what would be missing. `AppErrorBoundary` sits inside them and handles every
// realistic crash with real, translated copy. Between them, no failure anywhere
// in the tree can still produce the white screen this replaces.
ReactDOM.createRoot(document.getElementById('root')).render(
  <RootErrorBoundary>
    {window.location.pathname.startsWith('/docs') && offersPersian()
      ? (
        <ThemeProvider>
          <React.Suspense fallback={null}>
            <DocsApp />
          </React.Suspense>
        </ThemeProvider>
      )
      : window.location.pathname.startsWith('/erx/tamin-return') && isIranMarket()
      ? (
        <ThemeProvider>
          <I18nProvider>
            <React.Suspense fallback={null}>
              <TaminReturnPage />
            </React.Suspense>
          </I18nProvider>
        </ThemeProvider>
      )
      : (
        <ThemeProvider>
          <I18nProvider>
            {/* Points the design kit's own copy — the calendar chrome, the
                date picker's validation — at this panel's catalogue. Inside
                I18nProvider so it reads the same context every screen does. */}
            <KitStrings />
            <ToastProvider>
              <AppErrorBoundary>
                <App />
              </AppErrorBoundary>
            </ToastProvider>
          </I18nProvider>
        </ThemeProvider>
      )}
  </RootErrorBoundary>
);
